import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Star, Users, Info, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';

interface Player {
  id: string;
  name: string;
  username?: string;
  position?: string;
  isStarter?: boolean;
  playerType?: string;
}

interface StartingLineupManagerProps {
  gameId: string;
  teamSide: 'home' | 'away';
  teamName: string;
  gameTableType?: 'games' | 'venue_games' | null;
  onLineupChange: (starters: string[]) => void;
  canEdit: boolean;
  refreshKey?: number;
}

export const StartingLineupManager: React.FC<StartingLineupManagerProps> = ({
  gameId,
  teamSide,
  teamName,
  gameTableType,
  onLineupChange,
  canEdit,
  refreshKey
}) => {
  const [attendingPlayers, setAttendingPlayers] = useState<Player[]>([]);
  const [startingLineup, setStartingLineup] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttendingPlayers();
  }, [gameId, teamSide, refreshKey]);

  const loadAttendingPlayers = async () => {
    try {
      setLoading(true);
      
      // Primary source: game_rosters_live — all players with 'ready' status or is_present=true
      const { data: rosterData, error: rosterError } = await supabase
        .from('game_rosters_live')
        .select('*')
        .eq('game_id', gameId)
        .eq('team_side', teamSide);

      if (rosterError) {
        console.error('Error loading roster for lineup:', rosterError);
        setAttendingPlayers([]);
        return;
      }

      // Filter to players who are ready/available (not injured/unavailable)
      const availablePlayers = (rosterData || []).filter(p => 
        p.is_present === true || 
        !['injured', 'unavailable', 'no_show'].includes(p.game_day_status || '')
      );

      let playersToProcess = availablePlayers;

      if (playersToProcess.length === 0) {
        setAttendingPlayers([]);
        return;
      }

      const players: Player[] = [];

      // Get player info for each player
      for (const playerRecord of playersToProcess) {
        let playerInfo = null;
        if (playerRecord.player_type === 'supabase') {
          const { data } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', playerRecord.player_id)
            .maybeSingle();
          playerInfo = data;
        } else {
          const { data } = await supabase
            .from('internal_users')
            .select('username')
            .eq('id', playerRecord.player_id)
            .maybeSingle();
          playerInfo = data;
        }

        const displayName = playerInfo?.username ? `@${playerInfo.username}` : 'Unknown Player';

        players.push({
          id: playerRecord.player_id,
          name: displayName,
          username: playerInfo?.username,
          position: 'Player',
          playerType: playerRecord.player_type
        });
      }

      setAttendingPlayers(players);
      
      // Get existing starting lineup from the correct table
      const lineupField = teamSide === 'home' ? 'home_starting_lineup' : 'away_starting_lineup';
      let existingLineup: string[] = [];
      
      console.log('Loading starting lineup from table type:', gameTableType);
      
      if (gameTableType === 'venue_games') {
        const { data: gameData } = await supabase
          .from('venue_games')
          .select(lineupField)
          .eq('id', gameId)
          .maybeSingle();
        existingLineup = gameData?.[lineupField] || [];
      } else if (gameTableType === 'games') {
        const { data: gameData } = await supabase
          .from('games')
          .select(lineupField)
          .eq('id', gameId)
          .maybeSingle();
        existingLineup = gameData?.[lineupField] || [];
      } else {
        // Fallback: try both tables
        const { data: venueGameData } = await supabase
          .from('venue_games')
          .select(lineupField)
          .eq('id', gameId)
          .maybeSingle();
        
        if (venueGameData?.[lineupField]) {
          existingLineup = venueGameData[lineupField];
        } else {
          const { data: gameData } = await supabase
            .from('games')
            .select(lineupField)
            .eq('id', gameId)
            .maybeSingle();
          existingLineup = gameData?.[lineupField] || [];
        }
      }

      console.log('Loaded existing lineup:', existingLineup);
      setStartingLineup(existingLineup);
      onLineupChange(existingLineup);

    } catch (error) {
      console.error('Error loading attending players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerToggle = async (playerId: string, isStarter: boolean) => {
    const newLineup = isStarter 
      ? [...startingLineup, playerId]
      : startingLineup.filter(id => id !== playerId);

    setStartingLineup(newLineup);
    onLineupChange(newLineup);

    // Save to database
    if (canEdit) {
      try {
        const lineupField = teamSide === 'home' ? 'home_starting_lineup' : 'away_starting_lineup';
        
        console.log('Saving starting lineup to table type:', gameTableType, 'lineup:', newLineup);
        
        // Update the correct table based on gameTableType
        if (gameTableType === 'venue_games') {
          await supabase
            .from('venue_games')
            .update({ [lineupField]: newLineup })
            .eq('id', gameId);
        } else if (gameTableType === 'games') {
          await supabase
            .from('games')
            .update({ [lineupField]: newLineup })
            .eq('id', gameId);
        } else {
          // Fallback: try both tables
          const venueUpdate = supabase
            .from('venue_games')
            .update({ [lineupField]: newLineup })
            .eq('id', gameId);

          const gameUpdate = supabase
            .from('games')
            .update({ [lineupField]: newLineup })
            .eq('id', gameId);

          // Try both updates, at least one should succeed
          await Promise.allSettled([venueUpdate, gameUpdate]);
        }

        // Also update player_game_roles table
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('player_game_roles')
            .upsert({
              game_id: gameId,
              player_id: playerId,
              player_type: attendingPlayers.find(p => p.id === playerId)?.playerType || 'supabase',
              team_side: teamSide,
              role: isStarter ? 'starter' : 'bench',
              assigned_by: user.id
            });
        }
      } catch (error) {
        console.error('Error saving starting lineup:', error);
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Starting Lineup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading players...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="h-5 w-5" />
          Starting Lineup - {teamName}
          <Badge variant="outline">
            {startingLineup.length} starter{startingLineup.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
        <CardDescription>
          Designate which attending players will be in the starting lineup
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>View Only</AlertTitle>
            <AlertDescription>
              You can only view the starting lineup. Contact the coach or team owner to make changes.
            </AlertDescription>
          </Alert>
        )}

        {attendingPlayers.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Attending Players</h3>
            <p className="text-muted-foreground">
              Players must confirm their attendance before they can be added to the starting lineup.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {attendingPlayers.map((player) => {
              const isStarter = startingLineup.includes(player.id);
              return (
                <div key={player.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                  <Checkbox
                    id={`starter-${player.id}`}
                    checked={isStarter}
                    onCheckedChange={(checked) => handlePlayerToggle(player.id, checked as boolean)}
                    disabled={!canEdit}
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`starter-${player.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {player.name}
                    </label>
                    {player.username && (
                      <p className="text-xs text-muted-foreground">@{player.username}</p>
                    )}
                  </div>
                  {isStarter && (
                    <Badge variant="default" className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      Starter
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {startingLineup.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800 mb-2">
              <CheckCircle className="h-4 w-4" />
              <span className="font-medium">Starting Lineup Set</span>
            </div>
            <p className="text-green-700 text-sm">
              {startingLineup.length} player{startingLineup.length !== 1 ? 's' : ''} designated as starters. 
              This lineup will be used for team readiness confirmation.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};